import Document from "~/models/read/Document";
import Student from "~/models/read/User/Student";
import PlagiarismPdf from "~/models/read/PlagiarismPdf";
import Invoice from "~/models/read/Invoice";
import Service from "~/models/read/Service";

const STATUS = {
    COMPLETED: 'complete',
    NEW: 'not_started',
    PROCESSING: 'processing',
}

export default class PlagiarismOrder {
  private readonly _id: any;
  private _status: string;
  private readonly _document: Document;
  private readonly _similarityScore: any;
  private readonly _paidAt: Date;
  private readonly _completedAt: Date|null;
  private readonly _services: Array<Service>;
  private readonly _student: Student;
  private readonly _estimatedMinutesToComplete: number;
  private readonly _shouldBeCompletedAt: Date;
  private _pdfRequestedAt: Date|null;
  private readonly _pdf: PlagiarismPdf|null;
  private readonly _oscEnabled: boolean;
  private readonly _hasGrammarCheck: boolean;
  private readonly _relatedFreemiumPlagiarismCheckOrderId: string|null;
  private readonly _links: object;
  private _invoices: Invoice[];
  private readonly _isBundled: boolean;
  private readonly _bundleTotal: number;
  private readonly _tenant: string;

  constructor({
    id,
    status,
    document,
    similarityScore,
    paidAt,
    completedAt,
    services,
    student,
    estimatedMinutesToComplete,
    shouldBeCompletedAt,
    pdfRequestedAt,
    pdf,
    oscEnabled,
    checkGrammarCheck,
    links,
    freeCheck,
    isBundled,
    bundleTotal,
    tenant
  }) {
    this._id = id;
    this._status = status;
    this._document = new Document(document);
    this._similarityScore = similarityScore;
    this._paidAt = new Date(paidAt);
    this._completedAt = completedAt ? new Date(completedAt) : null;
    this._services = services ? services.map(s => new Service(s)) : null;
    this._student = new Student(student);
    this._estimatedMinutesToComplete = estimatedMinutesToComplete;
    this._shouldBeCompletedAt = new Date(shouldBeCompletedAt);
    this._pdfRequestedAt = pdfRequestedAt ? new Date(pdfRequestedAt) : null;
    this._pdf = pdf ? new PlagiarismPdf(pdf) : null;
    this._oscEnabled = oscEnabled;
    this._hasGrammarCheck = checkGrammarCheck;
    this._relatedFreemiumPlagiarismCheckOrderId = freeCheck ? freeCheck.id : null;
    this._links = links;
    this._invoices = [];
    this._isBundled = isBundled;
    this._bundleTotal = bundleTotal;
    this._tenant = tenant;
  }

  get id(): any {
    return this._id;
  }

  get status(): string {
    return this._status;
  }

  get document(): Document {
    return this._document;
  }

  get similarityScore(): any {
    return this._similarityScore;
  }

  get paidAt(): Date {
    return this._paidAt;
  }

  get completedAt(): Date | null {
    return this._completedAt;
  }

  get isCompleted() : boolean {
      return this._status === STATUS.COMPLETED;
  }

  get isOlderThanTwelveMonths() : boolean {
      return this._paidAt.getTime() < new Date().setMonth(new Date().getMonth() - 12);
  }

  get services(): Array<Service> {
    return this._services;
  }

  get student(): Student {
    return this._student;
  }

  get estimatedMinutesToComplete(): number {
    return this._estimatedMinutesToComplete;
  }

  get shouldBeCompletedAt(): Date {
    return this._shouldBeCompletedAt;
  }

  get shouldHaveBeenCompleted() : boolean {
    return new Date() > this._shouldBeCompletedAt;
  }

  get links(): object {
    return this._links;
  }

  requestPdf() {
      this._pdfRequestedAt = new Date();
  }

  get pdfRequestedAt(): Date | null {
    return this._pdfRequestedAt;
  }

  get pdf(): PlagiarismPdf | null {
    return this._pdf;
  }

  get oscEnabled(): boolean {
    return this._oscEnabled;
  }

  addInvoices(invoices) {
    this._invoices = invoices;
  }

  get additionalPaymentInvoices() {
    let invoices = this._invoices.filter(i => i.isOpen() && i.total.isPositive());
    if (invoices.length) {
        this._status = 'additional_payment';
    }
    return invoices;
  }

  get refundInvoices() {
    return this._invoices.filter(i => i.isOpen() && i.total.isNegative());
  }

  get hasGrammarCheck(): boolean {
    return this._hasGrammarCheck;
  }

  get relatedFreemiumPlagiarismCheckOrderId(): string|null {
    return this._relatedFreemiumPlagiarismCheckOrderId;
  }

  get isBundled(): boolean {
    return this._isBundled;
  }

  get bundleTotal(): number {
    return this._bundleTotal;
  }

  get hasTenantAIEnabled(): boolean {
    return this._tenant === 'AI_ENABLED';
  }

  get hasTenantUnified(): boolean {
    return this._tenant === 'UNIFIED';
  }
}
